import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, error, checkAuth, refreshAuth } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // Avoid noisy logs on each render; log only on state transitions inside effect

  // Try to refresh auth if we have a token but not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      console.log('ProtectedRoute: Checking auth...');
      checkAuth();
    }
  }, [isLoading, isAuthenticated, error, checkAuth]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('auth.checking_auth')}</p>
        </div>
      </div>
    );
  }

  if (error && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">{t('auth.auth_error')}</p>
            <p>{error}</p>
          </div>
          <button
            onClick={() => window.location.href = '/login'}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            {t('auth.go_to_login')}
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

export function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect to admin panel if already authenticated
    return <Navigate to="/panel/dashboard" replace />;
  }

  return children;
}
